import React from 'react'
import {
  GridDrilldownPanelView,
  GridPanelLayout,
  RenderHeaderFn,
  RenderIconFn
} from '.'
import { TabHeadingConfig } from '../tab-nav/tab-headings'
import { TabContentConfig } from '../tab-nav/tab-content-sections'
import { generateTabsComponents } from '../tab-nav'
import { css } from '@emotion/css'
import { AngularErrorBoundary } from '../../ng/react-bridge/error-boundary'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { leftOffset } from './grid-drilldown-panel-view'

interface GridDrilldownPanelViewWithTabsProps<TTabs extends string> {
  readonly layout: GridPanelLayout
  readonly defaultTabID: TTabs
  readonly renderIcon: RenderIconFn
  readonly renderHeader: RenderHeaderFn
  readonly tabHeadingConfigs: Array<TabHeadingConfig<TTabs>>
  readonly renderActions: () => React.ReactNode
  readonly tabContentConfigs: Array<TabContentConfig<TTabs>>
  readonly tabContentContainerID?: string
}

const smallScreenMediaQuery = '@media (max-width: 992px)'

export function GridDrilldownPanelViewWithTabs<TTabs extends string>({
  layout,
  defaultTabID,
  renderIcon,
  renderHeader,
  tabHeadingConfigs,
  renderActions,
  tabContentConfigs,
  tabContentContainerID
}: GridDrilldownPanelViewWithTabsProps<TTabs>) {
  const { TabsManager, TabHeadings, TabHeadingsFooter, TabContentSections } =
    generateTabsComponents<TTabs>({
      defaultTabID
    })

  const showWhiteBackgroundColor = layout === 'page'
  const showHorizontalRule = layout === 'panel'

  return (
    <AngularErrorBoundary
      children={() => (
        <TabsManager>
          <GridDrilldownPanelView
            showHorizontalRule={false}
            layout={layout}
            renderIcon={renderIcon}
            renderHeader={(renderHeaderProps) => (
              <>
                {renderHeader(renderHeaderProps)}
                <TabHeadings
                  tabHeadingConfigs={
                    tabHeadingConfigs.length === 1 ? [] : tabHeadingConfigs
                  }
                  showBackgroundColor={showWhiteBackgroundColor}
                  renderExtra={
                    <span
                      className={css`
                        list-style-type: none;

                        visibility: hidden;
                        @media (max-width: 992px) {
                          visibility: visible;
                        }
                      `}
                    >
                      {renderActions()}
                    </span>
                  }
                />
              </>
            )}
            renderContent={({ iconSize }) => {
              return (
                <>
                  <div
                    id="tab-heading-active-indicator"
                    className={css`
                      margin-left: ${leftOffset + iconSize}px;
                      ${smallScreenMediaQuery} {
                        width: 98%;
                      }
                    `}
                  >
                    <TabHeadingsFooter
                      showHorizontalRule={showHorizontalRule}
                      tabHeadingConfigs={tabHeadingConfigs}
                    />
                  </div>
                  <TabContentSections
                    tabContentConfigs={tabContentConfigs.map((config) => {
                      return {
                        ...config,
                        content: () => (
                          <TabContentContainer
                            showWhiteBackgroundColor={showWhiteBackgroundColor}
                            iconSize={iconSize}
                            id={tabContentContainerID}
                          >
                            <AngularErrorBoundary
                              children={() => <config.content />}
                            />
                          </TabContentContainer>
                        )
                      }
                    })}
                  />
                </>
              )
            }}
          />
        </TabsManager>
      )}
    />
  )
}

function TabContentContainer({
  showWhiteBackgroundColor,
  iconSize,
  children,
  id
}: {
  readonly showWhiteBackgroundColor: boolean
  readonly iconSize: number
  readonly children: React.ReactNode
  readonly id?: string
}) {
  return (
    <div
      id={id}
      className={css`
        ${showWhiteBackgroundColor === true
          ? `padding: ${iconSize}px;`
          : `padding: 0 ${iconSize}px;`};
        ${showWhiteBackgroundColor === true &&
        `background-color: ${theme.color.white};`};
        padding-right: 0;
      `}
    >
      {children}
    </div>
  )
}
